import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../../images/logo/logo.png';
import { useUserStore } from '../../stores/userStore';
import { useNotifications } from '../../contexts/NotificationContext';

const ResetPassword: React.FC = () => {
  const { resetPassword, isLoading, error, passwordResetSuccess, clearError, setPasswordResetSuccess } = useUserStore();
  const { success: showSuccess, errorNotification: showError } = useNotifications();
  const navigate = useNavigate();
  
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Reset state when component mounts
  useEffect(() => {
    setPasswordResetSuccess(false);
    clearError();
    return () => {
      setPasswordResetSuccess(false);
      clearError();
    };
  }, [setPasswordResetSuccess, clearError]);
  
  // Show error notification when reset fails
  useEffect(() => {
    if (error && !isLoading) {
      showError(error, 'Password Reset Failed');
      clearError();
    }
  }, [error, isLoading, showError, clearError]);
  
  // Dark mode handling. useColorMode (the hook the rest of the app uses)
  // stores this JSON-encoded (i.e. '"dark"', with quotes) via
  // useLocalStorage — a plain string comparison never matched that.
  useEffect(() => {
    const savedTheme = localStorage.getItem('color-theme');
    const isDark = savedTheme === '"dark"' || savedTheme === 'dark';
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      showError('Reset token is missing. Please use the link from your email.', 'Invalid Request');
      return;
    }
    
    if (!password || password.length < 8) {
      showError('Password must be at least 8 characters long', 'Validation Error');
      return;
    }
    
    if (password !== confirmPassword) {
      showError('Passwords do not match', 'Validation Error');
      return;
    }

    try {
      const result = await resetPassword(token, password);
      if (result) {
        showSuccess('Your password has been reset successfully.', 'Password Reset');
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/auth/signin');
        }, 3000);
      }
    } catch (err) {
      console.error('Error resetting password:', err);
    }
  };

  return (
    <div className='flex justify-center border items-center h-screen w-screen'>
      <div className="min-h-screen bg-white dark:bg-boxdark-2">
        <div className="flex flex-wrap">
          {/* Left side with logo and background */}
          <div className="hidden xl:block xl:w-1/2">
            <div className="relative h-screen w-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-green-800 opacity-90"></div>
              <div className="relative flex h-full items-center justify-center p-10">
                <div className="text-center">
                  <img 
                    src={Logo} 
                    alt="Re:Loam" 
                    className="mx-auto mb-8 h-32 w-auto opacity-90" 
                  />
                  <h1 className="mb-4 text-4xl font-bold text-white">Welcome to Re:Loam</h1>
                  <p className="text-xl text-green-100">Your land management solution</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side with form */}
          <div className="w-full xl:w-1/2">
            <div className="flex h-screen flex-col items-center justify-center p-4 sm:p-12.5 xl:p-17.5">
              <div className="mb-6 flex justify-center">
                <img 
                  src={Logo} 
                  alt="Re:L Logo" 
                  className="h-16 w-auto dark:invert" 
                />
              </div>
              
              <h2 className="mb-4 text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
                Reset Your Password
              </h2>
              
              {!passwordResetSuccess ? (
                <>
                  <p className="mb-9 text-gray-600 dark:text-gray-300">Enter your new password below</p>
                  
                  {!token && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg max-w-md">
                      <p>Invalid or missing reset token. Please make sure you're using the link from your email.</p>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="w-full max-w-md">
                    <div className="mb-6">
                      <label className="mb-2.5 block font-medium text-black dark:text-white">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          name="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter new password"
                          autoComplete="new-password"
                          className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                        />
                        <span className="absolute right-4 top-4">
                          <svg
                            className="fill-current"
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g opacity="0.5">
                              <path
                                d="M16.1547 6.80626V5.91251C16.1547 3.16251 14.0922 0.825009 11.4797 0.618759C10.0359 0.481259 8.59219 0.996884 7.52656 1.95938C6.46094 2.92188 5.84219 4.29688 5.84219 5.70626V6.80626C3.84844 7.18438 2.33594 8.93751 2.33594 11.0688V17.2906C2.33594 19.5594 4.19219 21.3813 6.42656 21.3813H15.5016C17.7703 21.3813 19.6266 19.525 19.6266 17.2563V11C19.6609 8.93751 18.1484 7.21876 16.1547 6.80626ZM8.55781 3.09376C9.31406 2.40626 10.3109 2.06251 11.3422 2.16563C13.1641 2.33751 14.6078 3.98751 14.6078 5.91251V6.70313H7.38906V5.67188C7.38906 4.70938 7.80156 3.78126 8.55781 3.09376ZM18.1141 17.2906C18.1141 18.7 16.9453 19.8688 15.5359 19.8688H6.46094C5.05156 19.8688 3.91719 18.7344 3.91719 17.325V11.0688C3.91719 9.52189 5.15469 8.28438 6.70156 8.28438H15.2953C16.8422 8.28438 18.1141 9.52188 18.1141 11V17.2906Z"
                                fill=""
                              />
                              <path
                                d="M10.9977 11.8594C10.5852 11.8594 10.207 12.2031 10.207 12.65V16.2594C10.207 16.6719 10.5508 17.05 10.9977 17.05C11.4102 17.05 11.7883 16.7063 11.7883 16.2594V12.6156C11.7883 12.2031 11.4102 11.8594 10.9977 11.8594Z"
                                fill=""
                              />
                            </g>
                          </svg>
                        </span>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="mb-2.5 block font-medium text-black dark:text-white">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          name="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm password"
                          autoComplete="new-password"
                          className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                        />
                        <span className="absolute right-4 top-4">
                          <svg
                            className="fill-current"
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g opacity="0.5">
                              <path
                                d="M16.1547 6.80626V5.91251C16.1547 3.16251 14.0922 0.825009 11.4797 0.618759C10.0359 0.481259 8.59219 0.996884 7.52656 1.95938C6.46094 2.92188 5.84219 4.29688 5.84219 5.70626V6.80626C3.84844 7.18438 2.33594 8.93751 2.33594 11.0688V17.2906C2.33594 19.5594 4.19219 21.3813 6.42656 21.3813H15.5016C17.7703 21.3813 19.6266 19.525 19.6266 17.2563V11C19.6609 8.93751 18.1484 7.21876 16.1547 6.80626ZM8.55781 3.09376C9.31406 2.40626 10.3109 2.06251 11.3422 2.16563C13.1641 2.33751 14.6078 3.98751 14.6078 5.91251V6.70313H7.38906V5.67188C7.38906 4.70938 7.80156 3.78126 8.55781 3.09376ZM18.1141 17.2906C18.1141 18.7 16.9453 19.8688 15.5359 19.8688H6.46094C5.05156 19.8688 3.91719 18.7344 3.91719 17.325V11.0688C3.91719 9.52189 5.15469 8.28438 6.70156 8.28438H15.2953C16.8422 8.28438 18.1141 9.52188 18.1141 11V17.2906Z"
                                fill=""
                              />
                              <path
                                d="M10.9977 11.8594C10.5852 11.8594 10.207 12.2031 10.207 12.65V16.2594C10.207 16.6719 10.5508 17.05 10.9977 17.05C11.4102 17.05 11.7883 16.7063 11.7883 16.2594V12.6156C11.7883 12.2031 11.4102 11.8594 10.9977 11.8594Z"
                                fill=""
                              />
                            </g>
                          </svg>
                        </span>
                      </div>
                    </div>
                    
                    <div className="mb-5">
                      <input
                        type="submit"
                        value={isLoading ? "Resetting..." : "Reset Password"}
                        disabled={isLoading || !token}
                        className={`w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition ${
                          isLoading || !token ? 'opacity-75 cursor-not-allowed' : 'hover:bg-opacity-90'
                        }`}
                      />
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center max-w-md">
                  <div className="mb-6 flex justify-center">
                    <div className="rounded-full bg-green-100 p-4">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-12 w-12 text-green-600" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M5 13l4 4L19 7" 
                        />
                      </svg>
                    </div>
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-black dark:text-white">
                    Password Reset Successful
                  </h3>
                  <p className="mb-6 text-gray-600 dark:text-gray-300">
                    Your password has been reset successfully. You will be redirected to the login page shortly.
                  </p>
                </div>
              )}
              
              <div className="mt-6 text-center">
                <p>
                  Remember your password?{' '}
                  <Link to="/auth/signin" className="inline-flex min-h-[40px] items-center text-primary">
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
